"use client";

import React, { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  X,
  ChevronDown,
  AlertCircle,
  Check,
  ArrowRight,
  ArrowLeft,
  Lock,
  Mail,
  Phone,
  Globe,
  ShieldCheck,
  ExternalLink,
  Sparkles,
  Wallet,
  CreditCard,
} from "lucide-react";
import toast from "react-hot-toast";

interface Country {
  code: string;
  name: string;
  dialCode: string;
}

interface PhoneValidation {
  isValid: boolean;
  error?: string;
}

const COUNTRIES: Country[] = [
  { code: "US", name: "United States", dialCode: "+1" },
  { code: "GB", name: "United Kingdom", dialCode: "+44" },
  { code: "CA", name: "Canada", dialCode: "+1" },
  { code: "AU", name: "Australia", dialCode: "+61" },
  { code: "DE", name: "Germany", dialCode: "+49" },
  { code: "FR", name: "France", dialCode: "+33" },
  { code: "NL", name: "Netherlands", dialCode: "+31" },
  { code: "ES", name: "Spain", dialCode: "+34" },
  { code: "IT", name: "Italy", dialCode: "+39" },
  { code: "SE", name: "Sweden", dialCode: "+46" },
  { code: "CH", name: "Switzerland", dialCode: "+41" },
];

type PayMethod =
  | "GUEST_CHECKOUT_APPLE_PAY"
  | "GUEST_CHECKOUT_GOOGLE_PAY"
  | "CARD";
type ModalView = "form" | "processing" | "celebrating" | "checkout";

const STEP_LABELS = ["Details", "Verify", "Pay"] as const;

interface CoinbaseHeadlessOnrampModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    country: string;
    countryCode: string;
    email: string;
    phoneNumber: string;
    paymentMethod: string;
  }) => Promise<{
    orderId: string;
    paymentLink: string;
    mode?: "embed" | "redirect";
  }>;
  /** Polls the order status (returns a normalized status string or null). */
  onCheckStatus?: (orderId: string) => Promise<string | null>;
  amount: number;
  theme?: "dark" | "light";
}

export function CoinbaseHeadlessOnrampModal({
  isOpen,
  onClose,
  onSubmit,
  onCheckStatus,
  amount,
  theme = "dark",
}: CoinbaseHeadlessOnrampModalProps) {
  const isDark = theme !== "light";

  const [view, setView] = useState<ModalView>("form");
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [country, setCountry] = useState<Country>(COUNTRIES[0]);
  const [email, setEmail] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [payMethod, setPayMethod] = useState<PayMethod>(
    "GUEST_CHECKOUT_APPLE_PAY",
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showDialCodeDropdown, setShowDialCodeDropdown] = useState(false);
  const [orderId, setOrderId] = useState<string>("");
  const [paymentLink, setPaymentLink] = useState<string>("");
  const [checkoutMode, setCheckoutMode] = useState<"embed" | "redirect">(
    "embed",
  );
  const [embedReady, setEmbedReady] = useState(false);
  const [embedHeight, setEmbedHeight] = useState(120);
  const [embedExpanded, setEmbedExpanded] = useState(false);
  const [paid, setPaid] = useState(false);
  // Google Pay only: true once the user has engaged the Google Pay sheet.
  // While set (and not yet `paid`), we cover the Coinbase iframe with our
  // own "Confirming…" layer so its false post-payment error never shows.
  const [payInFlight, setPayInFlight] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const countryRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const dialRef = useRef<HTMLDivElement>(null);
  const revealTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (countryRef.current && !countryRef.current.contains(e.target as Node))
        setShowCountryDropdown(false);
      if (dialRef.current && !dialRef.current.contains(e.target as Node))
        setShowDialCodeDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isOpen]);

  // Detect mobile and handle responsive behavior
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Listen for messages from the embedded Coinbase widget: best-effort
  // Best-effort auto-resize of the embedded button frame to fit its content.
  // NOTE: we deliberately do NOT infer success from postMessage — Coinbase
  // fires load events (e.g. "load_success") right after render, which would
  // falsely flip the screen to "paid". Completion is detected only by polling
  // the real order status below.
  useEffect(() => {
    if (view !== "checkout" || checkoutMode !== "embed") return;
    const onMsg = (e: MessageEvent) => {
      if (typeof e.origin !== "string" || !e.origin.includes("coinbase.com"))
        return;
      const d: any = e.data;
      const h =
        typeof d === "number"
          ? d
          : d?.height ?? d?.contentHeight ?? d?.data?.height;
      if (typeof h === "number" && h > 60 && h < 1200) {
        setEmbedHeight(Math.ceil(h));
        // Apple Pay only: if the widget grew well past the button (the QR /
        // scan step appeared), go full-size so nothing is clipped. Google Pay
        // opens a native sheet (no inline QR), so it stays compact.
        if (h > 220 && payMethod === "GUEST_CHECKOUT_APPLE_PAY")
          setEmbedExpanded(true);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [view, checkoutMode, payMethod, isMobile]);

  // Apple Pay only: detect a click/focus INTO the Coinbase iframe (the parent
  // window fires `blur` and document.activeElement becomes the iframe) and
  // open it full-size to fit the QR-scan step. Google Pay stays small.
  // On mobile, expand immediately on any interaction to show full payment flow.
  useEffect(() => {
    if (view !== "checkout" || checkoutMode !== "embed") return;
    if (payMethod !== "GUEST_CHECKOUT_APPLE_PAY") return;

    const onBlur = () => {
      setTimeout(() => {
        if (document.activeElement === iframeRef.current) setEmbedExpanded(true);
      }, 0);
    };

    const onTouchStart = () => {
      if (isMobile) setEmbedExpanded(true);
    };

    window.addEventListener("blur", onBlur);
    if (iframeRef.current) {
      iframeRef.current.addEventListener("touchstart", onTouchStart, true);
    }

    return () => {
      window.removeEventListener("blur", onBlur);
      if (iframeRef.current) {
        iframeRef.current.removeEventListener("touchstart", onTouchStart, true);
      }
    };
  }, [view, checkoutMode, payMethod, isMobile]);

  // Google Pay only: cover the iframe with our own "Confirming…" layer the
  // moment the Google Pay sheet is engaged. Coinbase's widget renders its
  // post-payment confirmation INSIDE this cross-origin frame, and with
  // Google Pay that step frequently falls back to a misleading "Google Pay
  // payment processing error" even though the charge succeeded and the USDC
  // is already on its way (GP's result/redirect handling can't fully
  // complete inside a sandboxed cross-origin frame). The backend order-
  // status poll below is the source of truth, but it lags the widget's
  // instant client-side error by several seconds — so we cover the widget
  // until the poll confirms (→ success screen). A bounded timeout reveals
  // the widget again if the user CANCELLED, so a cancelled sheet is never
  // stuck behind the cover.
  useEffect(() => {
    if (view !== "checkout" || checkoutMode !== "embed") return;
    if (payMethod !== "GUEST_CHECKOUT_GOOGLE_PAY") return;
    if (paid) return;

    const REVEAL_AFTER_OPEN_MS = 25000; // safety net if no focus event fires
    const REVEAL_AFTER_CLOSE_MS = 8000; // sheet closed, still unconfirmed → likely cancelled

    const clearReveal = () => {
      if (revealTimerRef.current !== null) {
        clearTimeout(revealTimerRef.current);
        revealTimerRef.current = null;
      }
    };
    // The reveal timer is cancelled by this effect's cleanup the instant
    // `paid` flips, so it only ever fires while the payment is unconfirmed.
    const scheduleReveal = (ms: number) => {
      clearReveal();
      revealTimerRef.current = setTimeout(() => setPayInFlight(false), ms);
    };

    // Sheet opened: the parent window loses focus to the iframe / native sheet.
    const engage = () => {
      setPayInFlight(true);
      scheduleReveal(REVEAL_AFTER_OPEN_MS);
    };
    const onBlur = () => {
      setTimeout(() => {
        if (document.activeElement === iframeRef.current) engage();
      }, 0);
    };
    const onTouchStart = () => engage();
    // Sheet closed / focus returned: by now any real charge is accepted, so
    // shorten the reveal window. If the poll hasn't confirmed shortly after,
    // it was almost certainly a cancel and we re-expose the GP button.
    const onFocus = () => {
      setPayInFlight((inFlight) => {
        if (inFlight) scheduleReveal(REVEAL_AFTER_CLOSE_MS);
        return inFlight;
      });
    };

    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    const node = iframeRef.current;
    if (node) node.addEventListener("touchstart", onTouchStart, true);

    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      if (node) node.removeEventListener("touchstart", onTouchStart, true);
      clearReveal();
    };
  }, [view, checkoutMode, payMethod, paid]);

  // Poll the real order status as the ONLY completion signal. We capture a
  // baseline on the first read and only flip to "paid" once the status
  // genuinely transitions into a paid state — so a freshly-created (unpaid)
  // order can never show success prematurely.
  useEffect(() => {
    if (view !== "checkout" || paid) return; // poll in BOTH embed & redirect modes
    if (!orderId || !onCheckStatus) return;
    let active = true;
    let baseline: string | null = null;
    const isPaidStatus = (s: string | null) =>
      s === "completed" || s === "in_progress";
    const check = async () => {
      const status = await onCheckStatus(orderId);
      if (!active) return;
      if (baseline === null) {
        baseline = status || "unknown"; // first read = baseline, never acts
        return;
      }
      if (status === "completed") {
        setPaid(true);
      } else if (status === "in_progress" && !isPaidStatus(baseline)) {
        setPaid(true);
      }
    };
    check(); // capture baseline immediately
    const id = setInterval(check, 1500); // Poll quickly so success surfaces fast (esp. under the GP cover)
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [view, checkoutMode, paid, orderId, onCheckStatus]);

  const validateEmail = useCallback(
    (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
    [],
  );

  const validatePhoneNumber = useCallback((phone: string): PhoneValidation => {
    const cleaned = phone.replace(/[^\d]/g, ""); // Remove non-digits (country code is selected separately)
    if (!cleaned) return { isValid: false, error: "Phone number is required" };

    if (cleaned.length < 9)
      return { isValid: false, error: "Phone number is too short" };
    if (cleaned.length > 15)
      return { isValid: false, error: "Phone number is too long" };

    return { isValid: true };
  }, []);

  const handleStep1Submit = useCallback(() => {
    // For card payments, skip email/phone and go directly to checkout
    if (payMethod === "CARD") {
      setErrors({});
      setView("processing");
      handleCardPaymentSubmit();
      return;
    }

    // For Apple Pay and Google Pay, require email
    const newErrors: Record<string, string> = {};
    if (!email.trim()) newErrors.email = "Email is required";
    else if (!validateEmail(email))
      newErrors.email = "Please enter a valid email address";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setCurrentStep(2);
  }, [email, validateEmail, payMethod]);

  const handleCardPaymentSubmit = useCallback(async () => {
    try {
      const orderDetails = await onSubmit({
        country: country.code,
        countryCode: country.dialCode,
        email: "", // Empty for card payments
        phoneNumber: "", // Empty for card payments
        paymentMethod: payMethod,
      });
      setOrderId(orderDetails.orderId);
      setPaymentLink(orderDetails.paymentLink);
      setCheckoutMode(orderDetails.mode || "redirect");

      // Do NOT auto-open the payment link. The checkout view shows an explicit
      // "Continue to secure checkout" CTA that the user taps when ready.

      // Celebrate, then reveal the checkout (embedded button or hosted CTA).
      setView("celebrating");
      setTimeout(() => setView("checkout"), 1900);
    } catch (error) {
      // Log actual error for debugging
      console.error("Card payment error:", error);

      // Extract meaningful error message from API response
      let errorMsg = "Unable to create order. Please try again.";

      if (error instanceof Error) {
        const message = error.message;
        // Try to extract errorMessage from Coinbase response
        if (message.includes("errorMessage")) {
          try {
            const match = message.match(/'errorMessage':\s*'([^']+)'/);
            if (match && match[1]) {
              // Clean up the error message (remove "InvalidRequest: " prefix)
              const cleanMsg = match[1].replace(/^InvalidRequest:\s*/, "");
              errorMsg = cleanMsg;
            }
          } catch (e) {
            // If parsing fails, use default message
          }
        }
      }

      setErrors({ submit: errorMsg });
      setView("form");
      toast.error(errorMsg);
    }
  }, [country, payMethod, onSubmit]);

  const handleStep2Submit = useCallback(async () => {
    const phoneValidation = validatePhoneNumber(phoneNumber);
    if (!phoneValidation.isValid) {
      setErrors({ phoneNumber: phoneValidation.error || "Invalid phone number" });
      return;
    }
    setErrors({});
    setView("processing");

    try {
      const orderDetails = await onSubmit({
        country: country.code,
        countryCode: country.dialCode,
        email: email.trim(),
        phoneNumber: phoneNumber.replace(/[^\d+]/g, ""),
        paymentMethod: payMethod,
      });
      setOrderId(orderDetails.orderId);
      setPaymentLink(orderDetails.paymentLink);

      // Hosted checkout for every method: the embedded Apple/Google Pay
      // iframe was unreliable (Coinbase's in-frame confirmation fails
      // intermittently), so all methods now open Coinbase's secure hosted
      // page in a new tab — same step-by-step UI as the card flow. The
      // embed iframe branch below is kept only as an unused fallback.
      setCheckoutMode("redirect");

      // Do NOT auto-open the payment link (including redirect mode). The checkout
      // view shows an explicit "Continue to secure checkout" CTA the user taps.

      // Celebrate, then reveal the checkout (embedded button or hosted CTA).
      setView("celebrating");
      setTimeout(() => setView("checkout"), 1900);
    } catch (error) {
      // Log actual error for debugging
      console.error("Order creation error:", error);

      // Extract meaningful error message from API response
      let errorMsg = "Unable to create order. Please check your details and try again.";

      if (error instanceof Error) {
        const message = error.message;
        // Try to extract errorMessage from Coinbase response
        if (message.includes("errorMessage")) {
          try {
            const match = message.match(/'errorMessage':\s*'([^']+)'/);
            if (match && match[1]) {
              // Clean up the error message (remove "InvalidRequest: " prefix)
              const cleanMsg = match[1].replace(/^InvalidRequest:\s*/, "");
              errorMsg = cleanMsg;
            }
          } catch (e) {
            // If parsing fails, use default message
          }
        }
      }

      setErrors({ submit: errorMsg });
      setView("form");
      toast.error(errorMsg);
    }
  }, [phoneNumber, validatePhoneNumber, country, email, payMethod, onSubmit, isMobile]);

  const handleGoBack = () => {
    if (currentStep === 2) {
      setErrors({});
      setCurrentStep(1);
    }
  };

  const resetAndClose = () => {
    if (view === "processing") return;
    onClose();
    setTimeout(() => {
      setView("form");
      setCurrentStep(1);
      setEmail("");
      setPhoneNumber("");
      setErrors({});
      setOrderId("");
      setPaymentLink("");
      setCheckoutMode("redirect");
      setPayMethod("GUEST_CHECKOUT_APPLE_PAY");
      setEmbedReady(false);
      setEmbedHeight(120);
      setEmbedExpanded(false);
      setPaid(false);
      setPayInFlight(false);
      if (revealTimerRef.current !== null) {
        clearTimeout(revealTimerRef.current);
        revealTimerRef.current = null;
      }
    }, 200);
  };

  const handleOpenPaymentLink = () => {
    if (paymentLink) {
      const win = window.open(paymentLink, "_blank", "noopener,noreferrer");
      if (!win) window.location.href = paymentLink;
    } else {
      toast.error("Payment link unavailable. Contact support with your order ID.");
    }
  };

  if (!isOpen) return null;

  const isCheckout = view === "checkout";
  // Which branding the hosted-checkout panel shows (title, logo, step copy).
  // Every method uses the hosted redirect flow now, so these follow the
  // selected METHOD rather than the checkout mode.
  const isCardMethod = payMethod === "CARD";
  const isApplePay = payMethod === "GUEST_CHECKOUT_APPLE_PAY";
  const isGooglePay = payMethod === "GUEST_CHECKOUT_GOOGLE_PAY";
  const activeStepIdx =
    view === "checkout" || view === "celebrating" || view === "processing"
      ? 2
      : currentStep - 1;

  // Render through a portal to <body> so the fixed overlay escapes any
  // ancestor with a `transform` / `filter` / `will-change`, which would
  // otherwise become the containing block for `position: fixed` and trap
  // the modal inside its parent instead of covering the whole viewport.
  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Add funds via Coinbase"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-md cb-backdrop"
        onClick={resetAndClose}
      />

      <div
        className={`relative w-full overflow-hidden cb-pop flex flex-col
          rounded-t-[28px] sm:rounded-[28px]
          transition-[max-width,max-height] duration-300
          sm:max-w-xl md:max-w-2xl
          ${embedExpanded && isMobile ? "max-h-[98vh]" : "max-h-[96vh] sm:max-h-[94vh]"}
          ${isDark ? "bg-[#0B0E14] text-white" : "bg-white text-gray-900"}`}
        style={{
          boxShadow: isDark
            ? "0 -1px 0 0 rgba(255,255,255,0.06) inset, 0 40px 120px -20px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.06)"
            : "0 40px 120px -20px rgba(15,23,42,0.35), 0 0 0 1px rgba(15,23,42,0.06)",
        }}
      >
        {/* Ambient brand glows */}
        <div className="pointer-events-none absolute -top-24 -left-16 w-72 h-72 rounded-full bg-[#0052FF]/25 blur-[90px]" />
        <div className="pointer-events-none absolute -top-20 right-0 w-64 h-64 rounded-full bg-[#61CE70]/20 blur-[90px]" />

        {/* ── Header ───────────────────────────────────────────── */}
        {!(embedExpanded && isMobile) && (
        <div className="relative px-6 sm:px-8 pt-6 sm:pt-7 pb-5 shrink-0">
          <div className="sm:hidden mx-auto mb-4 h-1.5 w-12 rounded-full bg-white/15" />

          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <div className="relative shrink-0">
                <div
                  className="w-12 h-12 sm:w-[52px] sm:h-[52px] rounded-2xl flex items-center justify-center shadow-lg"
                  style={{
                    background: "linear-gradient(135deg, #0052FF 0%, #1E6BFF 100%)",
                    boxShadow: "0 8px 24px -6px rgba(0,82,255,0.6)",
                  }}
                >
                  <svg viewBox="0 0 24 24" className="w-7 h-7 sm:w-8 sm:h-8" fill="none">
                    <circle cx="12" cy="12" r="11" fill="white" fillOpacity="0.16" />
                    <circle cx="12" cy="12" r="8.5" stroke="white" strokeWidth="1.6" />
                    <rect x="9.2" y="9.2" width="5.6" height="5.6" rx="1.1" fill="white" />
                  </svg>
                </div>
                <span className="absolute -bottom-1.5 -right-1.5 w-6 h-6 rounded-full bg-[#61CE70] flex items-center justify-center ring-2 ring-[#0B0E14]">
                  <Sparkles size={12} className="text-[#0B0E14]" strokeWidth={2.5} />
                </span>
              </div>

              <div>
                <h2 className="text-lg sm:text-[22px] font-bold tracking-tight leading-tight">
                  {isCheckout ? "Complete your payment" : "Add Funds"}
                </h2>
                <div className="flex items-center gap-1.5 mt-1">
                  <ShieldCheck size={13} className="text-[#61CE70]" strokeWidth={2.5} />
                  <span
                    className={`text-[11px] sm:text-xs font-medium ${
                      isDark ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Secured by Coinbase
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={resetAndClose}
              disabled={view === "processing"}
              className={`p-2 rounded-xl transition-all disabled:opacity-30 disabled:cursor-not-allowed ${
                isDark
                  ? "text-gray-400 hover:text-white hover:bg-white/5"
                  : "text-gray-400 hover:text-gray-900 hover:bg-gray-100"
              }`}
              aria-label="Close"
            >
              <X size={22} />
            </button>
          </div>

          {/* Stepper — hidden during the celebrate beat for a cleaner moment */}
          {view !== "celebrating" && (
            <div className="mt-6 flex items-center">
              {STEP_LABELS.map((label, idx) => {
                const done = idx < activeStepIdx;
                const active = idx === activeStepIdx;
                return (
                  <React.Fragment key={label}>
                    <div className="flex flex-col items-center gap-1.5">
                      <div
                        className={`relative w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all duration-300
                          ${
                            done
                              ? "bg-[#61CE70] text-[#0B0E14]"
                              : active
                                ? "text-white"
                                : isDark
                                  ? "bg-white/5 text-gray-500 ring-1 ring-white/10"
                                  : "bg-gray-100 text-gray-400 ring-1 ring-gray-200"
                          }`}
                        style={
                          active
                            ? {
                                background: "linear-gradient(135deg, #0052FF, #1E6BFF)",
                                boxShadow: "0 0 0 4px rgba(0,82,255,0.18)",
                              }
                            : undefined
                        }
                      >
                        {done ? <Check size={16} strokeWidth={3} /> : idx + 1}
                      </div>
                      <span
                        className={`text-[10px] sm:text-[11px] font-semibold tracking-wide ${
                          active
                            ? isDark
                              ? "text-white"
                              : "text-gray-900"
                            : done
                              ? "text-[#61CE70]"
                              : isDark
                                ? "text-gray-600"
                                : "text-gray-400"
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                    {idx < STEP_LABELS.length - 1 && (
                      <div className="flex-1 h-[3px] mx-2 sm:mx-3 -mt-5 rounded-full overflow-hidden bg-white/8">
                        <div
                          className="h-full rounded-full transition-all duration-500 ease-out"
                          style={{
                            width: idx < activeStepIdx ? "100%" : "0%",
                            background: "linear-gradient(90deg, #0052FF, #61CE70)",
                          }}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
        )}

        {!(embedExpanded && isMobile) && (
        <div className={`h-px shrink-0 ${isDark ? "bg-white/8" : "bg-gray-100"}`} />
        )}

        {/* ── Body ─────────────────────────────────────────────── */}
        <div className="relative -mt-8 flex-1 overflow-y-auto scrollbar-hide px-6 sm:px-8 py-6 sm:py-7">
          {/* Amount hero — step 1 only (step 2 + checkout use the compact strip) */}
          {view === "form" && currentStep === 1 && (
            <div
              className={`relative overflow-hidden rounded-2xl p-4 sm:p-5 mb-6 ${
                isDark ? "bg-white/[0.03]" : "bg-gray-50"
              }`}
              style={{
                boxShadow: isDark
                  ? "inset 0 0 0 1px rgba(255,255,255,0.06)"
                  : "inset 0 0 0 1px rgba(15,23,42,0.06)",
              }}
            >
              <div className="pointer-events-none absolute -right-8 -top-10 w-40 h-40 rounded-full bg-[#61CE70]/10 blur-3xl" />
              <div className="relative flex items-center justify-between">
                <div>
                  <p
                    className={`text-[11px] uppercase tracking-[0.12em] font-semibold ${
                      isDark ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    You're adding
                  </p>
                  <div className="flex items-baseline gap-1.5 mt-1">
                    <span className="text-3xl sm:text-[34px] font-extrabold tracking-tight tabular-nums">
                      ${amount.toFixed(2)}
                    </span>
                    <span
                      className={`text-sm font-semibold ${
                        isDark ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      USD
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ════════ FORM — STEP 1 ════════ */}
          {view === "form" && currentStep === 1 && (
            <div className="space-y-5 cb-fade">
              {/* Payment method selector */}
              <div>
                <FieldLabel icon={<Wallet size={14} />} isDark={isDark}>
                  How would you like to pay?
                </FieldLabel>
                <div className="grid grid-cols-3 gap-2 sm:gap-2.5">
                  <MethodPill
                    isDark={isDark}
                    active={payMethod === "CARD"}
                    onClick={() => setPayMethod("CARD")}
                  >
                    <span className="flex items-center gap-1.5">
                      <img src="/Visa-logo.png" alt="Visa" className="h-3 sm:h-3.5 object-contain" />
                      <img src="/mastercard-logo.png" alt="Mastercard" className="h-5 sm:h-6 object-contain" />
                    </span>
                  </MethodPill>
                  <MethodPill
                    isDark={isDark}
                    active={payMethod === "GUEST_CHECKOUT_APPLE_PAY"}
                    onClick={() => setPayMethod("GUEST_CHECKOUT_APPLE_PAY")}
                  >
                    <img src="/applepay-logo.png" alt="Apple Pay" className="max-h-5 sm:max-h-6 max-w-full object-contain scale-150" />
                  </MethodPill>
                  <MethodPill
                    isDark={isDark}
                    active={payMethod === "GUEST_CHECKOUT_GOOGLE_PAY"}
                    onClick={() => setPayMethod("GUEST_CHECKOUT_GOOGLE_PAY")}
                  >
                    <img src="/googlepay-logo.png" alt="Google Pay" className="max-h-5 sm:max-h-6 max-w-full object-contain" />
                  </MethodPill>
                </div>
                <div
                  className={`flex items-center gap-1.5 mt-2.5 text-xs ${
                    isDark ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  <ShieldCheck size={13} className="text-[#61CE70]" strokeWidth={2.5} />
                  <span>
                    {payMethod === "CARD"
                      ? "Debit, credit or bank. Works in every browser."
                      : payMethod === "GUEST_CHECKOUT_APPLE_PAY"
                        ? "One-tap, secure checkout with Apple Pay."
                        : "One-tap, secure checkout with Google Pay."}
                  </span>
                </div>
              </div>

              {/* Country */}
              <div ref={countryRef}>
                <FieldLabel icon={<Globe size={14} />} isDark={isDark}>
                  Country or region
                </FieldLabel>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCountryDropdown((s) => !s);
                      setShowDialCodeDropdown(false);
                    }}
                    className={`w-full px-4 py-3.5 rounded-2xl flex items-center justify-between transition-all text-left ${inputSurface(
                      isDark,
                      false,
                    )}`}
                  >
                    <span className="flex items-center gap-3">
                      <Flag code={country.code} size={26} />
                      <span className="font-semibold">{country.name}</span>
                    </span>
                    <ChevronDown
                      size={18}
                      className={`transition-transform duration-200 ${
                        showCountryDropdown ? "rotate-180" : ""
                      } ${isDark ? "text-gray-500" : "text-gray-400"}`}
                    />
                  </button>

                  {showCountryDropdown && (
                    <Dropdown isDark={isDark}>
                      {COUNTRIES.map((c) => (
                        <DropdownItem
                          key={c.code}
                          isDark={isDark}
                          selected={c.code === country.code}
                          onClick={() => {
                            setCountry(c);
                            setShowCountryDropdown(false);
                          }}
                        >
                          <Flag code={c.code} size={24} />
                          <span className="flex-1 font-medium">{c.name}</span>
                          <span
                            className={`text-xs tabular-nums ${
                              isDark ? "text-gray-500" : "text-gray-400"
                            }`}
                          >
                            {c.dialCode}
                          </span>
                          {c.code === country.code && (
                            <Check size={16} className="text-[#61CE70]" strokeWidth={3} />
                          )}
                        </DropdownItem>
                      ))}
                    </Dropdown>
                  )}
                </div>
              </div>

              {/* Email - Hidden for CARD payments */}
              {payMethod !== "CARD" && (
                <div>
                  <FieldLabel icon={<Mail size={14} />} isDark={isDark} required>
                    Email address
                  </FieldLabel>
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email) setErrors({ ...errors, email: "" });
                    }}
                    onBlur={() => {
                      if (email && !validateEmail(email))
                        setErrors({ ...errors, email: "Please enter a valid email" });
                    }}
                    onKeyDown={(e) => e.key === "Enter" && handleStep1Submit()}
                    placeholder="you@example.com"
                    className={`w-full px-4 py-3.5 rounded-2xl transition-all font-medium ${inputSurface(
                      isDark,
                      !!errors.email,
                    )} ${isDark ? "placeholder-gray-600" : "placeholder-gray-400"}`}
                  />
                  <FieldError isDark={isDark} error={errors.email} />
                  {!errors.email && (
                    <FieldHint isDark={isDark}>
                      Required for your receipt and payment verification
                    </FieldHint>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ════════ FORM — STEP 2 ════════ */}
          {view === "form" && currentStep === 2 && (
            <div className="space-y-5 cb-fade">
              <AmountStrip amount={amount} isDark={isDark} />
              <div>
                <FieldLabel icon={<Phone size={14} />} isDark={isDark} required>
                  Phone number
                </FieldLabel>
                <div className="flex gap-2.5">
                  <div ref={dialRef} className="relative shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setShowDialCodeDropdown((s) => !s);
                        setShowCountryDropdown(false);
                      }}
                      className={`h-full px-3.5 py-3.5 rounded-2xl flex items-center gap-2 font-semibold transition-all ${inputSurface(
                        isDark,
                        false,
                      )}`}
                    >
                      <Flag code={country.code} size={24} />
                      <span className="tabular-nums">{country.dialCode}</span>
                      <ChevronDown
                        size={15}
                        className={`transition-transform duration-200 ${
                          showDialCodeDropdown ? "rotate-180" : ""
                        } ${isDark ? "text-gray-500" : "text-gray-400"}`}
                      />
                    </button>

                    {showDialCodeDropdown && (
                      <Dropdown isDark={isDark} className="w-64">
                        {COUNTRIES.map((c) => (
                          <DropdownItem
                            key={c.code}
                            isDark={isDark}
                            selected={c.code === country.code}
                            onClick={() => {
                              setCountry(c);
                              setShowDialCodeDropdown(false);
                            }}
                          >
                            <Flag code={c.code} size={24} />
                            <span className="flex-1 font-medium text-sm">{c.name}</span>
                            <span
                              className={`text-xs tabular-nums font-semibold ${
                                isDark ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {c.dialCode}
                            </span>
                            {c.code === country.code && (
                              <Check size={15} className="text-[#61CE70]" strokeWidth={3} />
                            )}
                          </DropdownItem>
                        ))}
                      </Dropdown>
                    )}
                  </div>

                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel-national"
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(e.target.value);
                      if (errors.phoneNumber) setErrors({ ...errors, phoneNumber: "" });
                    }}
                    onBlur={() => {
                      if (phoneNumber) {
                        const v = validatePhoneNumber(phoneNumber);
                        if (!v.isValid)
                          setErrors({ ...errors, phoneNumber: v.error || "Invalid" });
                      }
                    }}
                    onKeyDown={(e) => e.key === "Enter" && handleStep2Submit()}
                    placeholder="555 555 0100"
                    className={`flex-1 min-w-0 px-4 py-3.5 rounded-2xl transition-all font-medium tabular-nums ${inputSurface(
                      isDark,
                      !!errors.phoneNumber,
                    )} ${isDark ? "placeholder-gray-600" : "placeholder-gray-400"}`}
                  />
                </div>
                <FieldError isDark={isDark} error={errors.phoneNumber} />
                {!errors.phoneNumber && (
                  <FieldHint isDark={isDark}>
                    Required for payment verification and fraud prevention
                  </FieldHint>
                )}
              </div>

              {/* Review chips */}
              <div
                className={`rounded-2xl p-4 space-y-3 ${
                  isDark ? "bg-white/[0.03]" : "bg-gray-50"
                }`}
                style={{
                  boxShadow: isDark
                    ? "inset 0 0 0 1px rgba(255,255,255,0.05)"
                    : "inset 0 0 0 1px rgba(15,23,42,0.05)",
                }}
              >
                <ReviewRow
                  isDark={isDark}
                  icon={<Wallet size={14} />}
                  label="Method"
                  value={
                    payMethod === "GUEST_CHECKOUT_APPLE_PAY"
                      ? "Apple Pay"
                      : payMethod === "GUEST_CHECKOUT_GOOGLE_PAY"
                        ? "Google Pay"
                        : "Card"
                  }
                />
                <ReviewRow
                  isDark={isDark}
                  icon={<Globe size={14} />}
                  label="Country"
                  value={
                    <span className="flex items-center gap-1.5">
                      <Flag code={country.code} size={18} />
                      {country.name}
                    </span>
                  }
                />
                <ReviewRow
                  isDark={isDark}
                  icon={<Mail size={14} />}
                  label="Email"
                  value={<span className="truncate max-w-[170px]">{email}</span>}
                />
              </div>
            </div>
          )}

          {/* ════════ PROCESSING ════════ */}
          {view === "processing" && (
            <div className="flex flex-col items-center justify-center py-12 sm:py-16 cb-fade">
              <div className="relative w-24 h-24">
                <div className="absolute inset-0 rounded-full bg-[#0052FF]/20 blur-xl cb-glow" />
                <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="44"
                    fill="none"
                    stroke={isDark ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.08)"}
                    strokeWidth="6"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="44"
                    fill="none"
                    stroke="url(#cbspin)"
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray="80 200"
                    className="cb-spin"
                  />
                  <defs>
                    <linearGradient id="cbspin" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#0052FF" />
                      <stop offset="100%" stopColor="#61CE70" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Lock size={28} className="text-[#61CE70] cb-float" strokeWidth={2.2} />
                </div>
              </div>
              <h3 className="mt-7 text-lg sm:text-xl font-bold tracking-tight">
                Creating your secure order
              </h3>
              <p className={`mt-1.5 text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Connecting to Coinbase…
              </p>
              <div className="mt-5 flex items-center gap-1.5">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="w-2 h-2 rounded-full bg-[#61CE70] cb-dot"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ════════ CELEBRATING ════════ */}
          {view === "celebrating" && (
            <div className="flex flex-col items-center justify-center py-14 sm:py-20 text-center cb-fade">
              <div className="relative w-28 h-28">
                {/* burst rings */}
                <span className="absolute inset-0 rounded-full bg-[#61CE70]/25 cb-burst" />
                <span
                  className="absolute inset-0 rounded-full bg-[#61CE70]/20 cb-burst"
                  style={{ animationDelay: "0.15s" }}
                />
                <div
                  className="absolute inset-3 rounded-full flex items-center justify-center"
                  style={{
                    background: "linear-gradient(135deg, #61CE70, #3aa64a)",
                    boxShadow: "0 16px 40px -8px rgba(97,206,112,0.7)",
                  }}
                >
                  <Check size={48} className="text-white cb-check" strokeWidth={3} />
                </div>
              </div>
              <h3 className="mt-7 text-2xl sm:text-[28px] font-extrabold tracking-tight">
                Order created!
              </h3>
              <p className={`mt-2 text-sm sm:text-base ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Opening your secure checkout…
              </p>
            </div>
          )}

          {/* ════════ CHECKOUT ════════ */}
          {view === "checkout" && (
            <div className="cb-fade space-y-4">
              {/* ── Payment confirmed ── */}
              {paid ? (
                <div className="cb-fade">
                  <div className="flex flex-col items-center text-center pt-2 pb-1">
                    <div className="relative w-20 h-20">
                      <div className="absolute inset-0 rounded-full bg-[#61CE70]/20 blur-xl cb-glow" />
                      <div
                        className="absolute inset-0 rounded-full flex items-center justify-center"
                        style={{
                          background: "linear-gradient(135deg, #61CE70, #3aa64a)",
                          boxShadow: "0 12px 32px -8px rgba(97,206,112,0.6)",
                        }}
                      >
                        <Check size={40} className="text-white cb-check" strokeWidth={3} />
                      </div>
                    </div>
                    <h3 className="mt-5 text-xl font-bold tracking-tight">
                      Payment received 🎉
                    </h3>
                    <p
                      className={`mt-1.5 text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Your money is on the way. It will land in your balance
                      shortly, usually within a minute.
                    </p>
                  </div>

                  {/* Summary */}
                  <div
                    className={`mt-5 rounded-2xl p-4 space-y-3 ${
                      isDark ? "bg-white/[0.03]" : "bg-gray-50"
                    }`}
                    style={{
                      boxShadow: isDark
                        ? "inset 0 0 0 1px rgba(255,255,255,0.05)"
                        : "inset 0 0 0 1px rgba(15,23,42,0.05)",
                    }}
                  >
                    <SummaryRow
                      isDark={isDark}
                      label="Amount"
                      value={
                        <span className="tabular-nums">${amount.toFixed(2)}</span>
                      }
                    />
                    <SummaryRow
                      isDark={isDark}
                      label="Paid with"
                      value={
                        isApplePay
                          ? "Apple Pay"
                          : isGooglePay
                            ? "Google Pay"
                            : "Card"
                      }
                    />
                    <SummaryRow
                      isDark={isDark}
                      label="Email"
                      value={
                        <span className="truncate max-w-[170px] inline-block align-bottom">
                          {email}
                        </span>
                      }
                    />
                    <SummaryRow
                      isDark={isDark}
                      label="Status"
                      value={
                        <span className="inline-flex items-center gap-1.5 text-[#61CE70]">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#61CE70] animate-pulse" />
                          Processing
                        </span>
                      }
                    />
                    {orderId && !orderId.startsWith("hosted") && (
                      <SummaryRow
                        isDark={isDark}
                        label="Reference"
                        value={
                          <span className="font-mono text-xs">
                            {orderId.slice(0, 8)}…
                          </span>
                        }
                      />
                    )}
                  </div>

                  <p
                    className={`mt-3 text-center text-xs ${
                      isDark ? "text-gray-500" : "text-gray-400"
                    }`}
                  >
                    You can track this top-up anytime on the Add Funds page.
                  </p>
                </div>
              ) : checkoutMode === "redirect" ? (
                /* ── Hosted Coinbase checkout (card / Apple Pay / Google Pay) ── */
                <>
                  <AmountStrip amount={amount} isDark={isDark} />
                  <div
                    className={`rounded-2xl overflow-hidden ${
                      isDark ? "bg-white/[0.03]" : "bg-gray-50"
                    }`}
                    style={{
                      boxShadow: isDark
                        ? "inset 0 0 0 1px rgba(255,255,255,0.06)"
                        : "inset 0 0 0 1px rgba(15,23,42,0.06)",
                    }}
                  >
                    <div className="p-6 sm:p-7">
                      {/* Header: icon + title + accepted cards */}
                      <div className="flex items-center gap-3.5">
                        <div className="relative shrink-0 w-12 h-12">
                          <div className="absolute inset-0 rounded-2xl bg-[#0052FF]/25 blur-md" />
                          <div
                            className="relative w-12 h-12 rounded-2xl flex items-center justify-center"
                            style={{
                              background: "linear-gradient(135deg, #0052FF, #1E6BFF)",
                              boxShadow: "0 8px 22px -8px rgba(0,82,255,0.6)",
                            }}
                          >
                            {isCardMethod ? (
                              <CreditCard size={24} className="text-white" />
                            ) : (
                              <Wallet size={24} className="text-white" />
                            )}
                            <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#61CE70] flex items-center justify-center ring-2 ring-[#0B0E14]">
                              <Lock size={11} className="text-[#0B0E14]" strokeWidth={2.8} />
                            </span>
                          </div>
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-lg font-bold tracking-tight">
                            {isApplePay
                              ? "Pay with Apple Pay"
                              : isGooglePay
                                ? "Pay with Google Pay"
                                : "Pay with your card"}
                          </h3>
                          <div className="flex items-center gap-1.5 mt-1.5">
                            {isApplePay ? (
                              <span className="h-6 px-2 rounded bg-white flex items-center shadow-sm">
                                <img src="/applepay-logo.png" alt="Apple Pay" className="h-4 object-contain" />
                              </span>
                            ) : isGooglePay ? (
                              <span className="h-6 px-2 rounded bg-white flex items-center shadow-sm">
                                <img src="/googlepay-logo.png" alt="Google Pay" className="h-4 object-contain" />
                              </span>
                            ) : (
                              <>
                                <span className="h-6 px-1.5 rounded bg-white flex items-center shadow-sm">
                                  <img src="/Visa-logo.png" alt="Visa" className="h-2.5 object-contain" />
                                </span>
                                <span className="h-6 px-1.5 rounded bg-white flex items-center shadow-sm">
                                  <img src="/mastercard-logo.png" alt="Mastercard" className="h-4 object-contain" />
                                </span>
                                <span className="h-6 px-1.5 rounded bg-white flex items-center shadow-sm">
                                  <img src="/amex.png" alt="Amex" className="h-4 object-contain" />
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Steps */}
                      <div
                        className={`mt-5 rounded-xl p-4 space-y-3 ${
                          isDark ? "bg-white/[0.03]" : "bg-white"
                        }`}
                      >
                        {[
                          "Tap the button to open the secure Coinbase checkout.",
                          isApplePay
                            ? "Confirm the payment with Apple Pay."
                            : isGooglePay
                              ? "Confirm the payment with Google Pay."
                              : "Enter your card details and confirm the payment.",
                          "Come back here. Your funds are added automatically.",
                        ].map((text, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div
                              className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                isDark
                                  ? "bg-[#0052FF]/15 text-[#5B8DFF]"
                                  : "bg-[#0052FF]/10 text-[#0052FF]"
                              }`}
                            >
                              {i + 1}
                            </div>
                            <span
                              className={`text-[13px] leading-snug ${
                                isDark ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              {text}
                            </span>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={handleOpenPaymentLink}
                        disabled={!paymentLink}
                        className="mt-5 w-full px-5 py-3.5 rounded-2xl font-bold text-sm sm:text-base text-white transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60"
                        style={{
                          background: "linear-gradient(135deg, #0052FF 0%, #1E6BFF 100%)",
                          boxShadow: "0 10px 28px -8px rgba(0,82,255,0.55)",
                        }}
                      >
                        Continue to secure checkout <ExternalLink size={17} strokeWidth={2.5} />
                      </button>
                    </div>
                    <div
                      className={`px-5 py-3 flex items-center justify-center gap-2 ${
                        isDark ? "bg-[#0052FF]/8" : "bg-[#0052FF]/6"
                      }`}
                    >
                      <ShieldCheck size={13} className="text-[#5B8DFF]" strokeWidth={2.5} />
                      <span
                        className={`text-[11px] font-medium ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        {isCardMethod
                          ? "Your card details stay private and never reach CashTap"
                          : "Your payment details stay private and never reach CashTap"}
                      </span>
                    </div>
                  </div>
                </>
              ) : paymentLink ? (
                /* ── Apple Pay / Google Pay → embedded inline button (no redirect) ── */
                <>
                  {/* Compact-mode chrome (hidden once expanded into the full
                      payment view, so the QR / scan step gets all the room).
                      On mobile, collapse this earlier to give more space to the iframe. */}
                  {!embedExpanded && !isMobile && (
                    <>
                      <AmountStrip amount={amount} isDark={isDark} />
                      <p
                        className={`text-center text-sm ${
                          isDark ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        Tap{" "}
                        <span className="font-semibold">
                          {isApplePay ? "Apple Pay" : "Google Pay"}
                        </span>{" "}
                        below. The payment sheet opens right here, no redirect.
                      </p>
                    </>
                  )}

                  {/* The Coinbase button, embedded inline. CSP frame-ancestors
                      whitelists our domain (via the `domain` order param), so
                      this renders in-app; the wallet sheet pops over the page.
                      Coinbase's widget body is #121212, so we match the panel
                      to that exact colour — the button reads as floating on our
                      surface, with no visible iframe edge. Expands to a full
                      view once the user starts the Apple/Google Pay flow. */}
                  <div
                    className="relative mx-auto w-full overflow-hidden rounded-2xl transition-all duration-300"
                    style={{
                      background: "#121212",
                    }}
                  >
                    {!embedReady && (
                      <div
                        className="absolute inset-0 flex items-center justify-center gap-2 z-10"
                        style={{ background: "#121212" }}
                      >
                        <div className="w-5 h-5 rounded-full border-[3px] border-[#0052FF]/30 border-t-[#0052FF] cb-spin" />
                        <span className="text-xs font-medium text-gray-400">
                          Loading {isApplePay ? "Apple Pay" : "Google Pay"}…
                        </span>
                      </div>
                    )}

                    {/* Google Pay: once the sheet is engaged, cover the
                        widget so its false "payment processing error" never
                        shows. The iframe stays mounted underneath (so the
                        Coinbase session isn't destroyed) until the poll flips
                        `paid` → success screen, or the bounded reveal timer
                        re-exposes the button on a cancel. */}
                    {isGooglePay && payInFlight && !paid && (
                      <div
                        className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2.5 px-6 text-center"
                        style={{ background: "#121212" }}
                      >
                        <div className="w-7 h-7 rounded-full border-[3px] border-[#61CE70]/30 border-t-[#61CE70] cb-spin" />
                        <p className="text-sm font-semibold text-white">
                          Confirming your payment…
                        </p>
                        <p className="text-xs text-gray-400">
                          Hang tight — this only takes a moment. Please don't
                          close this window.
                        </p>
                      </div>
                    )}

                    {/* Hide iframe once payment is detected to avoid showing error messages */}
                    {!paid && (
                      <iframe
                        ref={iframeRef}
                        key={paymentLink}
                        src={paymentLink}
                        title={isApplePay ? "Apple Pay" : "Google Pay"}
                        onLoad={() => setEmbedReady(true)}
                        allow="payment *; publickey-credentials-get *"
                        scrolling="no"
                        style={{
                          width: "100%",
                          height: embedExpanded
                            ? isMobile
                              ? "min(90vh, 100%)"
                              : "min(72vh, 680px)"
                            : embedHeight || "120px",
                          border: "none",
                          background: "#121212",
                          display: "block",
                        }}
                      />
                    )}
                  </div>

                  {!embedExpanded && (
                    <>
                      {/* Fallback for environments where the embed can't render */}
                      <button
                        type="button"
                        onClick={handleOpenPaymentLink}
                        className={`mx-auto block text-[11px] font-semibold transition-colors ${
                          isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"
                        }`}
                      >
                        Button not showing? Open securely ↗
                      </button>

                      {!isMobile && (
                      <div className="flex items-center justify-center gap-2">
                        <ShieldCheck size={13} className="text-[#61CE70]" strokeWidth={2.5} />
                        <span
                          className={`text-[11px] font-medium ${
                            isDark ? "text-gray-500" : "text-gray-400"
                          }`}
                        >
                          Secured by Coinbase · stays in the app
                        </span>
                      </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  <AmountStrip amount={amount} isDark={isDark} />
                  <div
                    className={`rounded-2xl p-6 text-center ${
                      isDark ? "bg-amber-500/8 text-amber-300" : "bg-amber-50 text-amber-800"
                    }`}
                    style={{ boxShadow: "inset 0 0 0 1px rgba(245,158,11,0.25)" }}
                  >
                    <AlertCircle size={26} className="mx-auto mb-2.5" />
                    <p className="text-sm font-semibold">
                      Checkout is taking longer than expected
                    </p>
                    <p className="text-xs mt-1.5 opacity-80">
                      Your order was created. Please contact support with your
                      order ID to finish paying.
                    </p>
                  </div>
                </>
              )}

              {orderId && !orderId.startsWith("hosted") && !paid && (
                <p className={`text-center text-[11px] ${isDark ? "text-gray-600" : "text-gray-400"}`}>
                  Order <span className="font-mono">{orderId.slice(0, 8)}…</span>
                </p>
              )}
            </div>
          )}

          {/* Submit error */}
          {errors.submit && view === "form" && (
            <div
              className={`mt-5 p-4 rounded-2xl flex items-start gap-3 ${
                isDark ? "bg-red-500/8 text-red-300" : "bg-red-50 text-red-700"
              }`}
              style={{ boxShadow: "inset 0 0 0 1px rgba(239,68,68,0.22)" }}
              role="alert"
            >
              <AlertCircle size={18} className="shrink-0 mt-0.5" />
              <p className="text-sm font-medium">{errors.submit}</p>
            </div>
          )}
        </div>

        {/* ── Footer ───────────────────────────────────────────── */}
        {(view === "form" || (view === "checkout" && !(embedExpanded && isMobile))) && (
          <div
            className={`relative px-6 sm:px-8 py-5 shrink-0 ${
              isDark ? "bg-white/[0.02]" : "bg-gray-50/60"
            }`}
            style={{
              boxShadow: isDark
                ? "inset 0 1px 0 0 rgba(255,255,255,0.06)"
                : "inset 0 1px 0 0 rgba(15,23,42,0.06)",
            }}
          >
            {view === "form" && (
              <div className="flex items-center gap-3">
                {currentStep === 2 && (
                  <button
                    type="button"
                    onClick={handleGoBack}
                    className={`shrink-0 px-4 py-3.5 rounded-2xl font-semibold text-sm transition-all active:scale-[0.97] flex items-center gap-1.5 ${
                      isDark
                        ? "bg-white/5 text-gray-300 hover:bg-white/10"
                        : "bg-white text-gray-700 hover:bg-gray-100 ring-1 ring-gray-200"
                    }`}
                  >
                    <ArrowLeft size={16} /> Back
                  </button>
                )}

                <PrimaryButton
                  onClick={currentStep === 1 ? handleStep1Submit : handleStep2Submit}
                >
                  {currentStep === 1
                    ? payMethod === "CARD"
                      ? "Continue to secure checkout"
                      : "Continue"
                    : "Create order"}
                  <ArrowRight size={18} strokeWidth={2.5} />
                </PrimaryButton>
              </div>
            )}

            <div className={`flex items-center justify-center gap-2 ${view === "form" ? "mt-3.5" : ""}`}>
              <Lock size={12} className={isDark ? "text-gray-600" : "text-gray-400"} />
              <span
                className={`text-[11px] font-medium ${
                  isDark ? "text-gray-600" : "text-gray-400"
                }`}
              >
                256-bit encrypted · Powered by Coinbase
              </span>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .cb-backdrop { animation: cbBackdrop .25s ease-out; }
        @keyframes cbBackdrop { from { opacity: 0 } to { opacity: 1 } }
        .cb-pop { animation: cbPop .32s cubic-bezier(0.16,1,0.3,1); }
        @keyframes cbPop {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .cb-fade { animation: cbFade .3s ease-out; }
        @keyframes cbFade {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .cb-spin { animation: cbSpin 1.1s linear infinite; transform-origin: 50% 50%; }
        @keyframes cbSpin { to { transform: rotate(360deg); } }
        .cb-glow { animation: cbGlow 2s ease-in-out infinite; }
        @keyframes cbGlow { 0%,100% { opacity: .5; } 50% { opacity: 1; } }
        .cb-float { animation: cbFloat 1.8s ease-in-out infinite; }
        @keyframes cbFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
        .cb-dot { animation: cbDot 1s ease-in-out infinite; }
        @keyframes cbDot { 0%,100% { opacity:.25; transform: scale(.8); } 50% { opacity:1; transform: scale(1); } }
        .cb-check { animation: cbCheck .45s cubic-bezier(0.16,1,0.3,1) .1s both; }
        @keyframes cbCheck { from { transform: scale(0) rotate(-12deg); } to { transform: scale(1) rotate(0); } }
        .cb-burst { animation: cbBurst 1.2s ease-out infinite; }
        @keyframes cbBurst {
          0%   { transform: scale(0.6); opacity: .7; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .cb-pop, .cb-fade, .cb-backdrop, .cb-check, .cb-burst { animation: none !important; }
        }
      `}</style>
    </div>,
    document.body,
  );
}

/* ── Sub-components ─────────────────────────────────────────── */

// Real flag images (flagcdn) — emoji flags don't render on Windows, which is
// why text codes showed instead. Images render identically on every platform.
function Flag({ code, size = 24 }: { code: string; size?: number }) {
  const lower = code.toLowerCase();
  return (
    <img
      src={`https://flagcdn.com/w40/${lower}.png`}
      srcSet={`https://flagcdn.com/w80/${lower}.png 2x`}
      width={size}
      height={Math.round(size * 0.75)}
      alt={code}
      loading="lazy"
      className="rounded-[4px] object-cover shrink-0"
      style={{
        width: size,
        height: Math.round(size * 0.75),
        boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
      }}
    />
  );
}

function MethodPill({
  isDark,
  active,
  onClick,
  children,
}: {
  isDark: boolean;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex items-center justify-center h-[54px] sm:h-[62px] px-2 rounded-2xl bg-white transition-all active:scale-[0.98] ${
        active
          ? "ring-2 ring-[#61CE70] shadow-[0_8px_24px_-10px_rgba(97,206,112,0.55)]"
          : isDark
            ? "ring-1 ring-white/10 hover:ring-white/25"
            : "ring-1 ring-gray-200 hover:ring-gray-300"
      }`}
    >
      {active && (
        <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#61CE70] flex items-center justify-center ring-2 ring-[#0B0E14]">
          <Check size={12} className="text-[#0B0E14]" strokeWidth={3.5} />
        </span>
      )}
      {children}
    </button>
  );
}

// Compact amount context strip, shown on the Verify and Pay steps so the
// buyer always sees what they're paying.
function AmountStrip({ amount, isDark }: { amount: number; isDark: boolean }) {
  return (
    <div
      className={`flex items-center justify-between rounded-2xl px-4 py-3 ${
        isDark ? "bg-white/[0.03]" : "bg-gray-50"
      }`}
      style={{
        boxShadow: isDark
          ? "inset 0 0 0 1px rgba(255,255,255,0.06)"
          : "inset 0 0 0 1px rgba(15,23,42,0.06)",
      }}
    >
      <span
        className={`text-xs font-semibold uppercase tracking-[0.1em] ${
          isDark ? "text-gray-500" : "text-gray-400"
        }`}
      >
        Amount
      </span>
      <div className="flex items-baseline gap-1.5">
        <span className="text-lg font-extrabold tracking-tight tabular-nums">
          ${amount.toFixed(2)}
        </span>
        <span
          className={`text-xs font-semibold ${
            isDark ? "text-gray-400" : "text-gray-500"
          }`}
        >
          USD
        </span>
      </div>
    </div>
  );
}

function inputSurface(isDark: boolean, hasError: boolean): string {
  if (hasError) {
    return isDark
      ? "bg-red-500/5 text-white ring-1 ring-red-500/50 focus:ring-2 focus:ring-red-500/60 focus:outline-none"
      : "bg-red-50 text-gray-900 ring-1 ring-red-400 focus:ring-2 focus:ring-red-400 focus:outline-none";
  }
  return isDark
    ? "bg-white/[0.04] text-white ring-1 ring-white/10 hover:ring-white/20 focus:ring-2 focus:ring-[#61CE70]/60 focus:bg-white/[0.06] focus:outline-none"
    : "bg-white text-gray-900 ring-1 ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-[#61CE70]/60 focus:outline-none";
}

function FieldLabel({
  children,
  icon,
  isDark,
  required,
}: {
  children: React.ReactNode;
  icon: React.ReactNode;
  isDark: boolean;
  required?: boolean;
}) {
  return (
    <label
      className={`flex items-center gap-1.5 text-[13px] font-semibold mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`}
    >
      <span className={isDark ? "text-gray-500" : "text-gray-400"}>{icon}</span>
      {children}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );
}

function FieldHint({
  children,
  isDark,
}: {
  children: React.ReactNode;
  isDark: boolean;
}) {
  return (
    <p className={`mt-2 text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
      {children}
    </p>
  );
}

function FieldError({ error, isDark }: { error?: string; isDark: boolean }) {
  if (!error) return null;
  return (
    <div className="flex items-center gap-1.5 mt-2" role="alert">
      <AlertCircle size={14} className={isDark ? "text-red-400" : "text-red-500"} />
      <p className={`text-xs font-medium ${isDark ? "text-red-400" : "text-red-600"}`}>
        {error}
      </p>
    </div>
  );
}

function Dropdown({
  children,
  isDark,
  className = "",
}: {
  children: React.ReactNode;
  isDark: boolean;
  className?: string;
}) {
  return (
    <div
      className={`absolute top-full left-0 right-0 mt-2 z-20 rounded-2xl overflow-hidden cb-fade ${className} ${
        isDark ? "bg-[#12161F]" : "bg-white"
      }`}
      style={{
        boxShadow: isDark
          ? "0 20px 50px -12px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.08)"
          : "0 20px 50px -12px rgba(15,23,42,0.25), 0 0 0 1px rgba(15,23,42,0.08)",
      }}
    >
      <div className="max-h-64 overflow-y-auto p-1.5">{children}</div>
    </div>
  );
}

function DropdownItem({
  children,
  isDark,
  selected,
  onClick,
}: {
  children: React.ReactNode;
  isDark: boolean;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full px-3 py-2.5 rounded-xl text-left flex items-center gap-3 transition-colors ${
        selected
          ? isDark
            ? "bg-[#61CE70]/12"
            : "bg-[#61CE70]/10"
          : isDark
            ? "hover:bg-white/5"
            : "hover:bg-gray-50"
      } ${isDark ? "text-gray-200" : "text-gray-800"}`}
    >
      {children}
    </button>
  );
}

function ReviewRow({
  icon,
  label,
  value,
  isDark,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  isDark: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span
        className={`flex items-center gap-2 text-xs font-medium ${
          isDark ? "text-gray-500" : "text-gray-400"
        }`}
      >
        {icon}
        {label}
      </span>
      <span className={`text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-800"}`}>
        {value}
      </span>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  isDark,
}: {
  label: string;
  value: React.ReactNode;
  isDark: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <span
        className={`text-xs font-medium ${isDark ? "text-gray-500" : "text-gray-400"}`}
      >
        {label}
      </span>
      <span
        className={`text-sm font-semibold ${isDark ? "text-gray-100" : "text-gray-900"}`}
      >
        {value}
      </span>
    </div>
  );
}

function PrimaryButton({
  children,
  onClick,
  disabled = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex-1 px-5 py-3.5 rounded-2xl font-bold text-sm sm:text-base text-white transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
      style={{
        background: "linear-gradient(135deg, #61CE70 0%, #4FB85F 100%)",
        boxShadow: disabled ? "none" : "0 10px 28px -8px rgba(97,206,112,0.6)",
      }}
    >
      {children}
    </button>
  );
}

export default CoinbaseHeadlessOnrampModal;
